import {
    CheckArgs,
    RuleResponse,
    RuleSummary,
    generateRuleResponse,
    removeNonAlphanumeric,
} from "./util";

export class WorkspaceNameChecker {
    static get error_messages() {
        return {
            cannotStartWithPunctuation:
                "Workspace name must start with an alphanumeric character",
            mustIncludeAlpha:
                "Workspace name must include an alphabetic character (A-Z)",
            cannotIncludeOnlyOrganisationName:
                "Workspace name cannot be identical to the Organisation name",
            cannotBeEmpty: "Workspace name cannot be empty",
            minimumLength: "Workspace name must be at least 3 characters",
        };
    }

    // Cannot begin with punctuation
    static cannotStartWithPunctuation({ name }: CheckArgs): RuleResponse {
        name = name.trim();

        if (!name.match(/^[A-Za-z0-9]/)) {
            return generateRuleResponse(
                false,
                WorkspaceNameChecker.error_messages.cannotStartWithPunctuation,
            );
        }

        return generateRuleResponse(true);
    }

    // Must contain alphabetical characters of some kind, cannot be only numbers
    static mustIncludeAlpha({ name }: CheckArgs): RuleResponse {
        name = name.trim();

        if (!name.match(/[A-Za-z]/)) {
            return generateRuleResponse(
                false,
                WorkspaceNameChecker.error_messages.mustIncludeAlpha,
            );
        }

        return generateRuleResponse(true);
    }

    // Must include more than the organisation name. Disregard all punctuation when comparing.
    static cannotIncludeOnlyOrganisationName({
        name,
        organisationName,
    }: CheckArgs): RuleResponse {
        name = removeNonAlphanumeric(name);
        organisationName = removeNonAlphanumeric(organisationName!);

        const nameWords = name.trim().toLowerCase().split(" ").sort();

        const organisationNameWords = organisationName
            .trim()
            .toLowerCase()
            .split(" ")
            .sort();

        if (nameWords.every((word) => organisationNameWords.includes(word))) {
            return generateRuleResponse(
                false,
                WorkspaceNameChecker.error_messages
                    .cannotIncludeOnlyOrganisationName,
            );
        }

        return generateRuleResponse(true);
    }

    // Must contain something
    static cannotBeEmpty({ name }: CheckArgs): RuleResponse {
        name = name.trim();

        if (!name) {
            return generateRuleResponse(
                false,
                WorkspaceNameChecker.error_messages.cannotBeEmpty,
            );
        }

        return generateRuleResponse(true);
    }

    // Must contain at least three characters
    static minimumLength({ name }: CheckArgs): RuleResponse {
        name = name.trim();

        if (name.length < 3) {
            return generateRuleResponse(
                false,
                WorkspaceNameChecker.error_messages.minimumLength,
            );
        }

        return generateRuleResponse(true);
    }

    static check(name: string, organisationName: string): RuleSummary {
        name = name.trim();

        // If it fails cannotBeEmpty, exit early and don't run any more checks until we have something to work with.
        if (!WorkspaceNameChecker.cannotBeEmpty({ name }).approved) {
            return {
                approved: false,
                responses: [WorkspaceNameChecker.cannotBeEmpty({ name })],
            };
        }

        const listOfTests = [
            WorkspaceNameChecker.minimumLength,
            WorkspaceNameChecker.cannotIncludeOnlyOrganisationName,
            WorkspaceNameChecker.cannotStartWithPunctuation,
            WorkspaceNameChecker.mustIncludeAlpha,
        ];

        const responses = listOfTests.map((test) => {
            return test({ name, organisationName });
        });

        return {
            approved: !responses.find((response) => {
                return response.approved === false;
            }),
            responses,
        };
    }
}
